import { useMemo } from 'react';

import { Box, ProgressStepper, Text } from '@zillow/constellation-10';

import { EXIT_CTA_LABEL } from '../../constants/WizardConstants';
import { STEP_DIRECTION } from './constants';

import type React from 'react';
import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { ShowDesktopViewBreakpoint, WizardBodyTemplateProps, WizardStepType } from './types';

export type { WizardBodyTemplateProps, WizardStepType, ShowDesktopViewBreakpoint };

export type WizardStepControllerProps = {
  currentStepKey: string;
  isOrdered?: boolean;
  onGoToStep: (stepKey: string, options?: { stepDirection?: string }) => void;
  progressSteps?: Array<{ key: string; label: string }>;
  steps: Array<WizardStepType>;
  wizardTitle?: React.ReactNode;
  onExitWizard?: () => void;
  labelAppearanceBreakpoint?: 'md' | 'xl' | 'xxl';
  showPageWizardDesktopViewBreakpoint?: ShowDesktopViewBreakpoint;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isWizardComplete?: boolean;
  entryPoint?: string;
  exitCTALabel?: string;
  /** When true, shows premium upgrade header for eligible listings. Default false for wizards that don't need it (e.g. listing claiming). */
  displayPremiumTopOfFunnelHeader?: boolean;
  /** When false, hides the progress stepper. Default true. */
  shouldRenderProgressStepper?: boolean;
  listingDetails?: GetUserPropertyDetailsResponseWebDto;
  allowInteractivity?: boolean;
  nonInteractiveProgressStepKeys?: Array<string>;
};

const WizardStepController = ({
  currentStepKey,
  isOrdered = true,
  onGoToStep,
  progressSteps = [],
  steps,
  wizardTitle,
  onExitWizard,
  labelAppearanceBreakpoint = 'md',
  showPageWizardDesktopViewBreakpoint = 'md',
  isBackDisabled = false,
  isNextDisabled = false,
  isWizardComplete = false,
  entryPoint = '',
  exitCTALabel = EXIT_CTA_LABEL.EXIT,
  displayPremiumTopOfFunnelHeader = false,
  shouldRenderProgressStepper = true,
  listingDetails,
  allowInteractivity = false,
  nonInteractiveProgressStepKeys = [],
}: WizardStepControllerProps) => {
  const firstValidProgressStepKey = progressSteps[0]?.key;
  const firstValidProgressStepIndex = steps.findIndex((step) => step.progressStepKey === firstValidProgressStepKey);
  const firstValidStepKey =
    firstValidProgressStepIndex >= 0 ? steps[firstValidProgressStepIndex]?.stepKey : steps[0]?.stepKey;

  const currentIndex = useMemo(() => {
    const idx = steps.findIndex((step) => step.stepKey === currentStepKey);
    return idx !== -1 ? idx : steps.findIndex((step) => step.stepKey === firstValidStepKey);
  }, [currentStepKey, firstValidStepKey, steps]);

  const currentStep = steps[currentIndex];
  const currentProgressStepKey = currentStep?.progressStepKey;
  const progressStepIndex = progressSteps.findIndex((p) => p.key === currentProgressStepKey);
  const currentProgressStepLabel = progressSteps[progressStepIndex]?.label;
  const totalSteps = steps.length;
  const currentStepIndex = steps.findIndex((step) => step.stepKey === currentStepKey) + 1 || 1;

  const stepsInCurrentGroup = steps.filter((s) => s.progressStepKey === currentProgressStepKey);
  const currentStepIndexInGroup = stepsInCurrentGroup.findIndex((s) => s.stepKey === currentStepKey) + 1;
  const totalStepsInGroup = stepsInCurrentGroup.length;

  const nonInteractiveProgressStepKeySet = useMemo(
    () => new Set(nonInteractiveProgressStepKeys),
    [nonInteractiveProgressStepKeys],
  );

  const handleProgressStepClick = (event: React.MouseEvent<HTMLAnchorElement>, nextIndex: number) => {
    event.preventDefault();
    const targetProgressKey = progressSteps[nextIndex]?.key;
    const firstStepInGroup = steps.find((s) => s.progressStepKey === targetProgressKey);
    if (firstStepInGroup) {
      onGoToStep(firstStepInGroup.stepKey);
    }
  };

  const progressStepperSteps = progressSteps.map((progressStep, index) => ({
    label: progressStep.label,
    interactive:
      allowInteractivity &&
      !nonInteractiveProgressStepKeySet.has(progressStep.key) &&
      (index < progressStepIndex || !isNextDisabled),
    ...(isWizardComplete || index < progressStepIndex ? { tone: 'complete' as const } : {}),
  }));

  const mobileDisplayStyles = {
    md: { base: 'block' as const, md: 'none' as const },
    xl: { base: 'block' as const, xl: 'none' as const },
    xxl: { base: 'block' as const, xxl: 'none' as const },
  }[labelAppearanceBreakpoint];

  const labelAppearanceStyles = {
    md: { base: 'none' as const, md: 'all' as const },
    xl: { base: 'none' as const, xl: 'all' as const },
    xxl: { base: 'none' as const, xxl: 'all' as const },
  }[labelAppearanceBreakpoint];

  const progressStepperToRender =
    shouldRenderProgressStepper && progressSteps.length > 0 && !currentStep?.hideProgressStepper ? (
      <Box
        css={{ backgroundColor: 'bg.neutral', display: 'flex', justifyContent: 'center' }}
        data-testid="ProgressStepperContainer"
      >
        <Box css={{ flex: 1, maxWidth: '100%' }} data-testid="ProgressStepperContent">
          <ProgressStepper
            activeStepIndex={progressStepIndex}
            steps={progressStepperSteps}
            labelAppearance={labelAppearanceStyles}
            aria-label="Wizard progress"
            onStepClick={allowInteractivity ? handleProgressStepClick : undefined}
          />
          <Box css={{ display: mobileDisplayStyles, marginLeft: '400' }}>
            <Text textStyle="body-sm-bold">{currentProgressStepLabel}</Text>
            {currentProgressStepLabel && <Text textStyle="body-sm">{` (${currentStepIndex}/${totalSteps})`}</Text>}
          </Box>
        </Box>
      </Box>
    ) : null;

  const CurrentWizardBodyTemplate = currentStep?.wizardBodyTemplate;
  if (!CurrentWizardBodyTemplate) return null;

  let nextStepKey: string | undefined;
  let previousStepKey: string | undefined;
  if (isOrdered) {
    const nextStep = steps[currentIndex + 1];
    const previousStep = steps[currentIndex - 1];
    nextStepKey = nextStep?.stepKey;
    previousStepKey = previousStep?.stepKey;
  } else {
    nextStepKey = currentStep?.nextStepKey;
    previousStepKey = currentStep?.previousStepKey;
  }

  const templateProps: WizardBodyTemplateProps = {
    entryPoint,
    exitCTALabel,
    onGoToStep,
    onGoToNextStep: nextStepKey ? () => onGoToStep(nextStepKey!, { stepDirection: STEP_DIRECTION.NEXT }) : null,
    onGoToPrevStep: previousStepKey
      ? () => onGoToStep(previousStepKey!, { stepDirection: STEP_DIRECTION.PREVIOUS })
      : null,
    hasPrevStep: !!previousStepKey,
    hasNextStep: !!nextStepKey,
    wizardTitle,
    totalSteps: totalStepsInGroup,
    currentStepIndex: currentStepIndexInGroup,
    onExitWizard,
    showDesktopViewBreakpoint: showPageWizardDesktopViewBreakpoint,
    displayPremiumTopOfFunnelHeader,
    progressStepper: progressStepperToRender,
    currentStepKey,
    isBackDisabled,
    isNextDisabled,
    listingDetails,
  };

  return <CurrentWizardBodyTemplate {...templateProps} />;
};

export default WizardStepController;
